import { useEffect, useRef, useState } from 'react';
import type { ReactElement, ReactNode } from 'react';
import { useMatches, useOutlet } from 'react-router';

import styles from './RouteFade.module.css';

/**
 * Перенос `lib/src/presentation/widgets/route_pages/fade_page.dart`.
 *
 * Переход между экранами — простое затухание.
 *
 * Стандартный переход Android (FadeForwardsPageTransitionsBuilder) на время
 * анимации кладёт под уходящую страницу `ColorScheme.surface` — у нас это
 * почти белый цвет, и между экранами мелькало светлое. Фон приложения
 * рисуется один раз под навигатором (`AppBackground`), поэтому страницам
 * достаточно проявиться и погаснуть поверх него.
 *
 * Во Flutter затухание делал навигатор: уходящая страница жила, пока шла
 * обратная анимация. React Router снимает её сразу, поэтому уходящий экран
 * удерживает [RouteFadeOutlet] — он держит предыдущее поддерево ещё
 * [ROUTE_FADE_DURATION_MS], и два экрана перекрёстно затухают, как в Dart.
 */

/** `transitionDuration` и `reverseTransitionDuration` у `FadePage`. */
export const ROUTE_FADE_DURATION_MS = 250;

/**
 * Какое совпадение отвечает за экран верхнего уровня: 0 — сама раскладка
 * с этим компонентом, 1 — страница под ней.
 */
const SCREEN_MATCH_INDEX = 1;

function pageClassName(entered: boolean, leaving: boolean): string {
  if (leaving) return `${styles.page} ${styles.leaving}`;
  if (entered) return `${styles.page} ${styles.entered}`;
  return styles.page;
}

/**
 * Первый кадр экран стоит прозрачным, со второго — едет к единице.
 *
 * Два кадра, а не один: браузер должен успеть применить `opacity: 0` как
 * начальное значение, иначе перехода не будет вовсе — он увидит только
 * конечное состояние.
 */
function useEnteredAfterPaint(): boolean {
  const [entered, setEntered] = useState(false);

  useEffect(() => {
    let inner = 0;
    const outer = requestAnimationFrame(() => {
      inner = requestAnimationFrame(() => setEntered(true));
    });
    return () => {
      cancelAnimationFrame(outer);
      cancelAnimationFrame(inner);
    };
  }, []);

  return entered;
}

export interface RouteFadeProps {
  /** Экран уходит: гаснет и больше не принимает касания. */
  readonly leaving?: boolean;
  readonly children: ReactNode;
}

/** Одна страница перехода — то, что в Dart собирал `FadePage`. */
export function RouteFade({ leaving = false, children }: RouteFadeProps): ReactElement {
  const entered = useEnteredAfterPaint();
  return <div className={pageClassName(entered, leaving)}>{children}</div>;
}

/** Экран на площадке перехода: ключ и его поддерево. */
interface RouteFadeScreen {
  readonly key: string;
  readonly outlet: ReactNode;
}

/**
 * `state.pageKey` из `_FadeTransition`.
 *
 * Ключом служит адрес экрана верхнего уровня, а не весь `pathname`:
 * страница источника (`ai-message-source`) вложена в чат, и при её
 * открытии чат обязан остаться на месте — во Flutter он тоже оставался под
 * прозрачной страницей, а не затухал.
 */
function useScreenKey(): string {
  const matches = useMatches();
  const screen = matches.at(SCREEN_MATCH_INDEX);
  return screen === undefined ? '/' : screen.pathname;
}

/**
 * Предыдущий экран, пока идёт затухание; `null` — перехода нет.
 *
 * Эффект без списка зависимостей намеренно: снимок последнего отрисованного
 * поддерева нужен после каждого рендера, иначе уходить будет устаревшая
 * версия экрана.
 */
function useLeavingScreen(screenKey: string, outlet: ReactNode): RouteFadeScreen | null {
  const [leaving, setLeaving] = useState<RouteFadeScreen | null>(null);
  const previousRef = useRef<RouteFadeScreen>({ key: screenKey, outlet });
  const timerRef = useRef<number | null>(null);

  useEffect(() => {
    return () => {
      if (timerRef.current !== null) window.clearTimeout(timerRef.current);
    };
  }, []);

  // Список зависимостей отсутствует намеренно: снимок последнего
  // отрисованного поддерева нужен после каждого рендера. Зациклиться нельзя —
  // `setLeaving` вызывается, только когда ключ экрана изменился, а к этому
  // моменту `previousRef` уже хранит новый ключ, и следующий заход выходит
  // на первой же проверке.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    const previous = previousRef.current;
    previousRef.current = { key: screenKey, outlet };
    if (previous.key === screenKey) return;
    // Переход начался раньше, чем закончился прошлый: старый экран
    // снимается сразу — во Flutter его так же вытеснял бы новый.
    if (timerRef.current !== null) window.clearTimeout(timerRef.current);
    setLeaving(previous);
    timerRef.current = window.setTimeout(() => {
      timerRef.current = null;
      setLeaving(null);
    }, ROUTE_FADE_DURATION_MS);
  });

  /*
    Возврат на экран, который ещё не догорел: аватары → язык → аватары
    быстрее, чем ROUTE_FADE_DURATION_MS. Состояние обновляет эффект, то есть
    уже после отрисовки, и в `leaving` на этот кадр лежит тот же экран, что
    и в текущем. Отдать оба нельзя: у двух записей совпали бы ключи, и React
    отрисовал бы экран дважды — на месте одной сетки аватаров оказывались
    две.

    Кто уходит на самом деле, знает `previousRef`: после отрисовки экрана
    языка там лежит именно он. Берём его — и переход остаётся переходом, без
    размонтирования. Во Flutter навигатор в этом случае гасил обратную
    анимацию и возвращал страницу на место, второй копии не заводил.
  */
  const stale = leaving !== null && leaving.key === screenKey;
  const candidate = stale ? previousRef.current : leaving;
  return candidate === null || candidate.key === screenKey ? null : candidate;
}

/**
 * Площадка перехода: в неё роутер отдаёт экраны через `<Outlet />`.
 *
 * Ставится корневой раскладкой в `router.tsx` — это то место, где во
 * Flutter стоял `Navigator` с `FadePage`.
 */
export function RouteFadeOutlet(): ReactElement {
  const outlet = useOutlet();
  const screenKey = useScreenKey();
  const leaving = useLeavingScreen(screenKey, outlet);
  const current: RouteFadeScreen = { key: screenKey, outlet };
  // Список с ключами, а не два соседних элемента: только по ключу React
  // узнаёт уходящий экран и сохраняет его поддерево. Иначе он смонтировал
  // бы его заново — чат поднял бы второе соединение.
  const screens: RouteFadeScreen[] = leaving === null ? [current] : [leaving, current];

  return (
    <div className={styles.stack}>
      {screens.map((screen) => (
        <RouteFade key={screen.key} leaving={screen.key !== screenKey}>
          {screen.outlet}
        </RouteFade>
      ))}
    </div>
  );
}
